/**
 * Clipboard capture helpers for chat inputs.
 *
 * Rich/large clipboard content (Word, Excel, PowerPoint, web pages, big plain
 * text) is stored as an external file attachment and referenced inline with a
 * token (`<clipboard 1>`) instead of dumping a raw text blob into the input.
 * Pasted images are likewise referenced inline with `<image 1>` tokens.
 *
 * The second half of this module backs the chip composer: raw input strings
 * (tokens verbatim) are parsed into Slate documents with inline-void chip
 * nodes and serialized back losslessly.
 */
import { Editor, Range, Transforms } from "slate";

// Plain/rich text pastes above this length are captured as files instead of
// being pasted inline into the textarea.
export const LARGE_TEXT_THRESHOLD = 1000;

/**
 * Extracts the relevant representations from a paste event's clipboardData.
 * @param {DataTransfer|null} clipboardData
 * @returns {{htmlString: string, plainString: string, imageItem: DataTransferItem|null}}
 */
export const extractClipboardParts = (clipboardData) => {
  const parts = { htmlString: "", plainString: "", imageItem: null };
  const items = clipboardData?.items;
  if (!items) return parts;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type === "text/html" && !parts.htmlString) {
      parts.htmlString = clipboardData.getData("text/html") || "";
    } else if (item.type === "text/plain" && !parts.plainString) {
      parts.plainString = clipboardData.getData("text/plain") || "";
    } else if (item.type.indexOf("image") === 0 && !parts.imageItem) {
      parts.imageItem = item;
    }
  }
  return parts;
};

/**
 * Decides whether a paste should be captured as a file attachment instead of
 * being pasted inline. Measures the VISIBLE text (plainString), not the
 * markup: a one-line copy from a styled web page easily carries >1KB of
 * HTML and must stay inline. HTML-only pastes (no plain flavour) fall back
 * to the markup length.
 * @param {{htmlString?: string, plainString?: string}} parts
 * @returns {boolean}
 */
export const shouldCaptureAsFile = ({ htmlString, plainString } = {}) => {
  if (plainString && plainString.length > LARGE_TEXT_THRESHOLD) return true;
  if (!plainString && htmlString && htmlString.length > LARGE_TEXT_THRESHOLD)
    return true;
  return false;
};

/**
 * Builds the File to upload for a captured paste. The HTML flavour is
 * preferred because it preserves Office formatting (tables, styles) for the
 * backend text extraction.
 * @param {{htmlString?: string, plainString?: string, counter: number}} params
 * @returns {File}
 */
export const buildClipboardFile = ({ htmlString, plainString, counter }) => {
  if (htmlString) {
    return new File([htmlString], `clipboard ${counter}.html`, {
      type: "text/html",
    });
  }
  return new File([plainString], `clipboard ${counter}.txt`, {
    type: "text/plain",
  });
};

/**
 * Detects the Office source app from clipboard HTML.
 * @param {string|null} htmlString
 * @returns {"excel"|"word"|"powerpoint"|null}
 */
export const detectClipboardSource = (htmlString) => {
  if (!htmlString) return null;
  if (
    /ProgId\s*=\s*Excel\.Sheet/i.test(htmlString) ||
    /urn:schemas-microsoft-com:office:excel/i.test(htmlString)
  ) {
    return "excel";
  }
  if (
    /ProgId\s*=\s*PowerPoint/i.test(htmlString) ||
    /urn:schemas-microsoft-com:office:powerpoint/i.test(htmlString)
  ) {
    return "powerpoint";
  }
  if (
    /ProgId\s*=\s*Word\.Document/i.test(htmlString) ||
    /urn:schemas-microsoft-com:office:word/i.test(htmlString)
  ) {
    return "word";
  }
  return null;
};

/**
 * User-facing label for a clipboard capture pill, e.g. "Excel capture 1".
 * @param {"excel"|"word"|"powerpoint"|null} source
 * @param {number} counter
 */
export const clipboardCaptureLabel = (source, counter) =>
  `${{ excel: "Excel", word: "Word", powerpoint: "PowerPoint" }[source] || "Clipboard"} capture ${counter}`;

/**
 * Maps an auto-generated capture file name/URL back to its inline token:
 * "clipboard 1.html"/"clipboard1.xlsx" -> "<clipboard 1>",
 * "image 2.png" -> "<image 2>". Returns null for regular user-uploaded
 * files (those never have tokens).
 * @param {string} nameOrUrl
 * @returns {string|null}
 */
export const captureTokenFromNameOrUrl = (nameOrUrl) => {
  // Uploads prepend a 13-digit timestamp for uniqueness (same convention as
  // detectFileName in the inputs): "1732801887278clipboard1.html"
  const base = decodeURIComponent(
    (nameOrUrl || "").split("?")[0].split("/").pop() || "",
  ).replace(/^\d{13}/, "");
  const clipboardMatch = /^clipboard[\s_]?(\d+)\./i.exec(base);
  if (clipboardMatch) return `<clipboard ${clipboardMatch[1]}>`;
  const imageMatch = /^image[\s_]?(\d+)\./i.exec(base);
  if (imageMatch) return `<image ${imageMatch[1]}>`;
  return null;
};

/**
 * Normalizes an uploaded file's basename for capture matching: strips the
 * 13-digit timestamp prefix and any spaces/underscores (the upload pipeline
 * sanitizes them away). "1732801887278clipboard 1.html" -> "clipboard1.html"
 * @param {string} baseName
 */
export const normalizeCaptureFileName = (baseName) =>
  (baseName || "")
    .replace(/^\d{13}/, "")
    .replace(/[\s_]/g, "")
    .toLowerCase();

/**
 * Inserts an inline reference token (e.g. `<clipboard 1>`, `<image 1>`) into a
 * plain textarea at the caret and syncs the controlled text state.
 * @param {HTMLTextAreaElement|null} textarea
 * @param {string} token
 * @param {(value: string) => void} [setText]
 */
export const insertTokenAtCursor = (textarea, token, setText) => {
  if (!textarea) return;
  const value = textarea.value || "";
  const hasSelection =
    typeof textarea.selectionStart === "number" &&
    typeof textarea.selectionEnd === "number";
  const start = hasSelection ? textarea.selectionStart : value.length;
  const end = hasSelection ? textarea.selectionEnd : start;
  const needsLeadingSpace = start > 0 && !/\s$/.test(value.slice(0, start));
  const insertion = `${needsLeadingSpace ? " " : ""}${token} `;
  const newValue = value.slice(0, start) + insertion + value.slice(end);
  textarea.value = newValue;
  const caret = start + insertion.length;
  textarea.setSelectionRange(caret, caret);
  textarea.focus();
  setText?.(newValue);
};

// ---------------------------------------------------------------------------
// Inline tokens (clipboard captures, pasted images, tool tags)
// ---------------------------------------------------------------------------

// Matches the tokens the input itself can contain: clipboard captures
// (`<clipboard 1>`), pasted image references (`<image 1>`) and tool tags
// inserted by the tool selectors (`~read_file~`, `~local_folder_access~`,
// custom functions like `~getWeather~`).
export const TOKEN_PATTERN = /(<clipboard \d+>|<image \d+>|~[A-Za-z0-9_\-]{2,40}~)/g;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Builds a regex matching "@Label" agent mentions for the given agent labels.
 * Longest labels first so "Sales Agent" wins over "Sales"; a mention must be
 * followed by whitespace or end-of-string so "@Sales" doesn't eat
 * "@Salesforce". Returns null when there are no labels.
 * @param {string[]} agentLabels
 * @param {string} flags
 */
export const buildAgentMentionRegex = (agentLabels = [], flags = "") => {
  const labels = (agentLabels || [])
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  if (labels.length === 0) return null;
  return new RegExp(`(@(?:${labels.map(escapeRegExp).join("|")}))(?=\\s|$)`, flags);
};

/**
 * Finds the first "@Label" agent mention in a text.
 * @returns {{mention: string, label: string, index: number}|null}
 */
export const findAgentMention = (text, agentLabels = []) => {
  const regex = buildAgentMentionRegex(agentLabels);
  if (!regex || !text) return null;
  const match = regex.exec(text);
  if (!match) return null;
  return { mention: match[1], label: match[1].slice(1), index: match.index };
};

/**
 * Splits an input value into plain-text and token segments (used to build
 * the chip composer's Slate children). The segments joined back together
 * always reproduce the original string.
 * @param {string} value
 * @returns {Array<{kind: "text"|"clipboard"|"image"|"tool", value: string}>}
 */
export const tokenizeForDisplay = (value) => {
  if (!value) return [];
  const segments = [];
  let lastIndex = 0;
  TOKEN_PATTERN.lastIndex = 0;
  let match;
  while ((match = TOKEN_PATTERN.exec(value)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ kind: "text", value: value.slice(lastIndex, match.index) });
    }
    const token = match[0];
    let kind = "tool";
    if (token.startsWith("<clipboard")) kind = "clipboard";
    else if (token.startsWith("<image")) kind = "image";
    segments.push({ kind, value: token });
    lastIndex = match.index + token.length;
  }
  if (lastIndex < value.length) {
    segments.push({ kind: "text", value: value.slice(lastIndex) });
  }
  return segments;
};

/**
 * Like tokenizeForDisplay but also recognizes "@Agent Label" mentions (chip
 * kind "agent") against the provided known agent labels.
 * @param {string} value
 * @param {string[]} agentLabels
 */
export const tokenizeWithAgentLabels = (value, agentLabels = []) => {
  const mentionRegex = buildAgentMentionRegex(agentLabels, "g");
  if (!mentionRegex || !value) return tokenizeForDisplay(value);

  const segments = [];
  let lastIndex = 0;
  let match;
  while ((match = mentionRegex.exec(value)) !== null) {
    if (match.index > lastIndex) {
      segments.push(
        ...tokenizeForDisplay(value.slice(lastIndex, match.index)),
      );
    }
    segments.push({ kind: "agent", value: match[1] });
    lastIndex = match.index + match[1].length;
  }
  if (lastIndex < value.length) {
    segments.push(...tokenizeForDisplay(value.slice(lastIndex)));
  }
  return segments;
};

// ---------------------------------------------------------------------------
// First-class chip composer (Slate-based)
// ---------------------------------------------------------------------------

// Kill-switch for the chip composer: when false the chat inputs render the
// original plain <textarea> with no token prettifying at all.
export const ENABLE_CHIP_COMPOSER = true;

export const CHIP_TYPE = "token-chip";

export const chipKindOfToken = (token) => {
  if (token.startsWith("<clipboard")) return "clipboard";
  if (token.startsWith("<image")) return "image";
  if (token.startsWith("@")) return "agent";
  return "tool";
};

// Delimiter-free label for the pill: "<clipboard 1>" -> "clipboard 1",
// "~read_file~" -> "read_file", "@Sales Agent" -> "Sales Agent"
export const chipLabelOfToken = (token) =>
  token.startsWith("@")
    ? token.slice(1)
    : token.replace(/^</, "").replace(/>$/, "").replace(/^~/, "").replace(/~$/, "");

export const makeChipNode = (token) => ({
  type: CHIP_TYPE,
  chipKind: chipKindOfToken(token),
  token,
  label: chipLabelOfToken(token),
  children: [{ text: "" }],
});

/**
 * Parses a raw input string into Slate children (text nodes + chip nodes).
 * Always starts and ends with a text node and never places two chips
 * adjacent without a text node between them (Slate renders require this).
 * @param {string} text
 * @param {string[]} agentLabels - known agent labels for "@Label" mentions
 * @returns {Array} children for a single paragraph block
 */
export const parseTextToSlateChildren = (text, agentLabels = []) => {
  const segments = tokenizeWithAgentLabels(text || "", agentLabels);
  const children = [];
  segments.forEach((segment) => {
    if (segment.kind === "text") {
      children.push({ text: segment.value });
    } else {
      if (
        children.length === 0 ||
        typeof children[children.length - 1].text !== "string"
      ) {
        children.push({ text: "" });
      }
      children.push(makeChipNode(segment.value));
    }
  });
  if (
    children.length === 0 ||
    typeof children[children.length - 1].text !== "string"
  ) {
    children.push({ text: "" });
  }
  return children;
};

/**
 * Initial Slate document for a raw input string.
 * @param {string} text
 * @param {string[]} agentLabels - known agent labels for "@Label" mentions
 */
export const makeComposerValue = (text, agentLabels = []) => [
  { type: "paragraph", children: parseTextToSlateChildren(text, agentLabels) },
];

/**
 * Serializes a Slate document back to the raw input string (chips become
 * their token text again, blocks are joined with newlines).
 * @param {Array} nodes - editor.children
 * @returns {string}
 */
export const serializeSlateToText = (nodes) => {
  const blockText = (node) => {
    if (typeof node?.text === "string") return node.text;
    if (node?.type === CHIP_TYPE) return node.token || "";
    return (node?.children || []).map(blockText).join("");
  };
  return (nodes || []).map(blockText).join("\n");
};

/**
 * Maps a string offset in the serialized text to a Slate point. Offsets
 * falling inside a chip's token snap to just after the chip (the first
 * following text node).
 * @param {Editor} editor
 * @param {number} offset
 * @returns {Point}
 */
export const stringOffsetToSlatePoint = (editor, offset) => {
  let consumed = 0;
  let fallback = { path: [0, 0], offset: 0 };
  const blocks = editor.children;
  for (let bi = 0; bi < blocks.length; bi++) {
    const children = blocks[bi]?.children || [];
    for (let ci = 0; ci < children.length; ci++) {
      const child = children[ci];
      if (typeof child.text === "string") {
        const start = consumed;
        consumed += child.text.length;
        fallback = { path: [bi, ci], offset: child.text.length };
        if (offset <= consumed) {
          return { path: [bi, ci], offset: Math.max(0, offset - start) };
        }
      } else {
        // chip: accounts for its token length in the string but holds no
        // selectable position
        consumed += (child.token || "").length;
      }
    }
    if (bi < blocks.length - 1) consumed += 1; // the newline join
  }
  return fallback;
};

/**
 * Maps a Slate point back to the string offset in the serialized text.
 * @param {Editor} editor
 * @param {Point} point
 * @returns {number}
 */
export const slatePointToStringOffset = (editor, point) => {
  let consumed = 0;
  const blocks = editor.children;
  for (let bi = 0; bi < blocks.length; bi++) {
    const children = blocks[bi]?.children || [];
    for (let ci = 0; ci < children.length; ci++) {
      const child = children[ci];
      if (bi === point.path[0] && ci === point.path[1]) {
        return (
          consumed +
          (typeof child.text === "string"
            ? Math.min(point.offset, child.text.length)
            : 0)
        );
      }
      consumed +=
        typeof child.text === "string" ? child.text.length : (child.token || "").length;
    }
    if (bi < blocks.length - 1) consumed += 1;
  }
  return consumed;
};

/**
 * Inserts a token as a chip at the caret (or at the end when there is no
 * selection), with the same spacing rules as insertTokenAtCursor. Works purely
 * in string-offset space: select → delete selection → leading space → chip →
 * normalize → trailing space → caret after it.
 * @param {Editor} editor
 * @param {string} token
 */
export const insertTokenAsChip = (editor, token) => {
  const fullText = serializeSlateToText(editor.children);
  const start = editor.selection
    ? slatePointToStringOffset(editor, Range.start(editor.selection))
    : fullText.length;
  const end = editor.selection
    ? slatePointToStringOffset(editor, Range.end(editor.selection))
    : start;

  const prevChar = start > 0 ? fullText[start - 1] : "";
  const nextChar = end < fullText.length ? fullText[end] : "";
  const needsLeadingSpace = start > 0 && !/\s/.test(prevChar);
  const needsTrailingSpace = nextChar === "" || !/\s/.test(nextChar);
  const insertion = `${needsLeadingSpace ? " " : ""}${token}${
    needsTrailingSpace ? " " : ""
  }`;
  const caretOffset = start + insertion.length;
  const afterChipOffset = start + (needsLeadingSpace ? 1 : 0) + token.length;

  if (!editor.selection) {
    Transforms.select(editor, Editor.end(editor, []));
  }
  Transforms.select(editor, {
    anchor: stringOffsetToSlatePoint(editor, start),
    focus: stringOffsetToSlatePoint(editor, end),
  });
  if (start !== end) Transforms.delete(editor);
  if (needsLeadingSpace) editor.insertText(" ");
  Transforms.insertNodes(editor, makeChipNode(token));
  // Normalization guarantees a text node exists right after the chip
  Editor.normalize(editor, { force: true });
  Transforms.select(editor, stringOffsetToSlatePoint(editor, afterChipOffset));
  if (needsTrailingSpace) editor.insertText(" ");
  Editor.normalize(editor, { force: true });
  Transforms.select(editor, stringOffsetToSlatePoint(editor, caretOffset));
};

